<template>
    <div id="workpost-cutting-page">
        <div
            class="timer"
            v-if="timer && !timer.finished"
        >
            <product-timer
                :loading="loading"
                @finish="onFinish"
                @pause="onPause"
                @unpause="startTimer(true)"
                @reporting-error="onErrorReporting"
                :can-report-error="true"
                :saving="saving"
                :pausing-or-restarting="pausingOrRestarting"
                :timer="timer"
                :all-finished="allFinished"
            />
        </div>
        <div>
            <div class="field">
                <ui5-label for="workpostcutting-scan-nr">
                    {{ trans('pages.workpostcutting.scan_label') }}
                </ui5-label>
                <ui5-input
                    type="Text"
                    id="workpostcutting-scan-nr"
                    @blur="scanNrBlur"
                    :value="scanNr"
                    @input="scanNr = $event.target.value"
                    :placeholder="
                        trans('pages.workpostcutting.scan_placeholder')
                    "
                />
                <div class="camera-icon">
                    <ui5-button
                        @click="scan"
                        :disabled="loading || (timer && !timer.finished)"
                    >
                        <i class="fa fa-camera fa-2x" />
                    </ui5-button>
                </div>
                <div
                    id="workpostcutting-pleatinglist-finished"
                    v-if="pleatinglist && pleatinglist.done"
                >
                    <h2>
                        {{ trans('pages.workpostcutting.pleating_finished') }}
                    </h2>
                </div>
            </div>

            <div class="half-grid">
                <div class="row">
                    <h2>
                        {{ trans('pages.workpostcutting.actions_pleating') }}
                    </h2>
                    <h2>
                        {{ trans('pages.workpostcutting.actions_cutting') }}
                    </h2>
                </div>
                <div class="row buttons">
                    <ui5-button
                        id="workpostcutting-print-all-PR-button"
                        @click="printAllPR"
                        :disabled="printing.PR || !pleatinglist"
                    >
                        <spinner v-if="printing.PR" />
                        {{ trans('pages.workpostcutting.print_all_pr') }}
                    </ui5-button>
                    <ui5-button
                        id="workpostcutting-print-current-line-PR-button"
                        @click="printPRforLine"
                        :disabled="
                            printing.linePR || getSelectedCutlines().length == 0
                        "
                    >
                        <spinner v-if="printing.linePR" />
                        {{ trans('pages.workpostcutting.print_line_pr') }}
                    </ui5-button>
                </div>
                <div class="row buttons">
                    <ui5-button :disabled="true">
                        {{ trans('pages.workpostcutting.report_error') }}
                    </ui5-button>
                </div>
                <div class="row tables">
                    <table
                        cellspacing="0"
                        cellpadding="0"
                        id="workpostcutting-profile-overview"
                        class="modern-table hoverable"
                        v-show="pleatinglist"
                    >
                        <thead v-if="pleatinglist">
                            <tr class="product-table-header-tr">
                                <th class="product-table-header-td">
                                    {{
                                        trans(
                                            'pages.workpostcutting.table_header.image'
                                        )
                                    }}
                                </th>
                                <th
                                    class="product-table-header-td"
                                    align="left"
                                >
                                    {{
                                        trans(
                                            'pages.workpostcutting.table_header.description'
                                        )
                                    }}
                                </th>
                                <th
                                    class="product-table-header-td"
                                    align="center"
                                >
                                    {{
                                        trans(
                                            'pages.workpostcutting.table_header.finished'
                                        )
                                    }}
                                </th>
                            </tr>
                        </thead>
                        <tbody v-if="pleatinglist && pleatinglist.products">
                            <template v-for="product in pleatinglist.products">
                                <tr
                                    class="product-table-item-tr"
                                    :class="{
                                        active:
                                            selectedProduct &&
                                            selectedProduct == product,
                                    }"
                                    @click="openProductRecords(product)"
                                    :data-product-id="product.productId"
                                >
                                    <td
                                        align="center"
                                        class="image-column"
                                        valign="center"
                                    >
                                        <img
                                            :src="product.imgUrl"
                                            @click="
                                                selectedImageProduct = product
                                            "
                                            :alt="product.productNr"
                                        />
                                    </td>
                                    <td
                                        align="left"
                                        class="product-column"
                                    >
                                        <div class="paintlistbold">
                                            {{ product.productNr }}
                                            <template v-if="isDebug">
                                                {{ product.productstatus }}
                                            </template>
                                        </div>
                                        <div class="paintlistitalic">
                                            {{ product.reference }}
                                        </div>
                                        <br />
                                        <div
                                            class="paintlistdefault"
                                            v-if="getCreationFilter(product)"
                                        >
                                            {{
                                                getCreationFilter(product)
                                                    .label
                                            }}:
                                            {{
                                                getCreationFilter(product)
                                                    .fieldvalue || 'null'
                                            }}
                                        </div>
                                        <template
                                            v-for="record in getRecords(
                                                product.productId
                                            )"
                                        >
                                            <template
                                                v-for="sawline in [
                                                    record.sawlist[0],
                                                ]"
                                            >
                                                <div
                                                    v-if="sawline"
                                                    class="paintlistdefault"
                                                    :data-record-id="record.ID"
                                                >
                                                    {{ sawline.name.trim() }}:
                                                    {{ sawline.value }}
                                                </div>
                                            </template>
                                        </template>
                                    </td>
                                    <td class="bol-column">
                                        <div
                                            class="bol"
                                            :style="getBollStyle(product)"
                                        />
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>

                    <table
                        id="workpostcutting-cutlines-overview"
                        class="modern-table"
                        cellspacing="0"
                        cellpadding="0"
                    >
                        <thead v-if="allCutlines && allCutlines.length > 0">
                            <tr class="product-table-header-tr">
                                <th
                                    class="product-table-header-td"
                                    align="center"
                                >
                                    {{
                                        trans(
                                            'pages.workpostcutting.cutlines_table.selection'
                                        )
                                    }}
                                </th>
                                <th
                                    class="product-table-header-td"
                                    align="center"
                                >
                                    {{
                                        trans(
                                            'pages.workpostcutting.cutlines_table.description'
                                        )
                                    }}
                                </th>
                                <th
                                    class="product-table-header-td"
                                    align="center"
                                >
                                    {{
                                        trans(
                                            'pages.workpostcutting.cutlines_table.length'
                                        )
                                    }}
                                </th>
                                <th
                                    class="product-table-header-td"
                                    align="center"
                                >
                                    {{
                                        trans(
                                            'pages.workpostcutting.cutlines_table.miter'
                                        )
                                    }}
                                </th>
                                <th
                                    class="product-table-header-td"
                                    align="center"
                                >
                                    {{
                                        trans(
                                            'pages.workpostcutting.cutlines_table.finished'
                                        )
                                    }}
                                </th>
                            </tr>
                        </thead>
                        <tbody v-if="allCutlines && allCutlines.length > 0">
                            <template v-for="(cutline, i) in allCutlines">
                                <tr
                                    :id="'sawlines-row-' + cutline.ID"
                                    :key="'sawline.' + cutline.ID"
                                    class="product-table-item-tr"
                                >
                                    <td align="center">
                                        <input
                                            class="sawlineSelectionCheckbox"
                                            v-model="cutline.selected"
                                            :checked="cutline.selected"
                                            @change="
                                                allCutlines.forEach((x) => {
                                                    x.selected = false;
                                                });
                                                cutline.selected =
                                                    $event.target.checked;
                                            "
                                            type="checkbox"
                                        />
                                    </td>
                                    <td align="left">
                                        {{ cutline.partOfLength }} -
                                        {{ cutline.name }} -
                                        {{ cutline.productNr }}
                                    </td>
                                    <td
                                        align="left"
                                        class="largerFont"
                                    >
                                        {{ cutline.value }}
                                    </td>
                                    <td align="center">
                                        {{ getMiter(cutline) }}
                                    </td>
                                    <td align="center">
                                        <ui5-checkbox
                                            id="sawlineFinishedCheckbox"
                                            class="sawlineFinishedCheckbox"
                                            :checked="cutline.sawn == '1'"
                                            @change="
                                                cutline.sawn = $event.target
                                                    .checked
                                                    ? '1'
                                                    : '0';
                                                onFinishedCheckbox(
                                                    cutline,
                                                    $event
                                                );
                                            "
                                        />
                                    </td>
                                </tr>
                                <template v-if="i < allCutlines.length - 1">
                                    <template
                                        v-if="
                                            cutline.partOfLength !==
                                            allCutlines[i + 1].partOfLength
                                        "
                                    >
                                        <tr
                                            class="divider"
                                            :key="'divider.' + cutline.ID"
                                        >
                                            <td colspan="4" />
                                        </tr>
                                    </template>
                                </template>
                            </template>
                        </tbody>
                    </table>
                </div>
                <div>
                    <spinner v-if="loadingCutlines" />
                </div>
            </div>
        </div>
        <image-modal
            v-if="selectedImageProduct"
            :source="selectedImageProduct.imgUrl"
            @modal-click="selectedImageProduct = null"
            :title="selectedImageProduct.productNr"
        />
    </div>
</template>
<script>
import axios from 'axios';
import api from '../api/api';
import Spinner from '../components/loader/Spinner';
import sawlistApi from '../api/sawlist';
import pleatingListApi from '../api/pleatingList';
import IsDebugMixin from '../mixins/is-debug';
import { showErrorMessage, showInfoMessage } from '../dialogs';
import productionTimesApi from '../api/productionTimes';
import { pleatingListMocker } from '../shared/qrMockers';
import workstationApi from '../api/workstation';
import ProductTimer from '../components/scanProducts/ProductTimer';
import ImageModal from '../components/modals/ImageModal';
import { createAggregatedTimer } from '../shared/timers';
import localization from '../mixins/localization';

const defaultData = {
    selectedImageProduct: null,
    scanNr: '',
    pleatinglist: null,
    allCutlines: null,
    selectedProduct: null,
    loadingCutlines: false,
    printing: {
        PR: false,
        linePR: false,
    },
    workstation: null,
    saving: false,
    pausingOrRestarting: false,
    timer: null,
    loadingTimer: false,
};

function getDefaultData() {
    return JSON.parse(JSON.stringify(defaultData));
}

export default {
    mixins: [IsDebugMixin, localization],
    components: { Spinner, ProductTimer, ImageModal },
    data: getDefaultData,
    mounted() {
        workstationApi
            .fetchByCustomView('#workpostcutting')
            .then((response) => {
                this.workstation = response.data.data;
            });

        this.$bus.$on('barcode-scan', (data) => {
            this.scanNr = data;
            this.scanNrBlur();
        });

        this.$bus.$on('qr_scanner.on_scan', (key, value) => {
            if (key === 'workpostcutting') {
                this.onScan(value);
            }
        });
    },
    beforeRouteLeave(to, from, next) {
        if (this.timer && !this.timer.finished) {
            this.onFinish();
        }
        next();
    },
    computed: {
        selectedPrinterPostParameter() {
            return this.$store.getters[
                'printers/getSelectedPrinterPostParameter'
            ]();
        },
        loading() {
            return this.loadingCutlines || this.loadingTimer;
        },
        allFinished() {
            if (!this.pleatinglist || !this.pleatinglist.profiles) {
                return true;
            }
            return this.pleatinglist.profiles
                .filter((x) => x.products)
                .every((x) => x.sawlinesSawn == 1);
        },
    },
    methods: {
        finish(pause) {
            const recordIds = this.timer.products.map(
                (x) => x.historyRecord.ID
            );
            return productionTimesApi
                .finishPleatingList(this.pleatinglist.id, recordIds, pause)
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.workpostcutting.errors.unknown_save')
                    );
                    this.timer.finished = false;
                });
        },
        onFinish() {
            this.saving = true;
            this.finish(false).then(() => {
                showInfoMessage(
                    this.trans(
                        'pages.workpostcutting.messages.production_time_saved'
                    )
                );

                pleatingListApi
                    .markFinished(
                        this.pleatinglist.id,
                        this.$store.getters['currentUser/getUserId']
                    )
                    .then(() => {
                        showInfoMessage(
                            this.trans(
                                'pages.workpostcutting.messages.pleating_list_finished'
                            )
                        );
                        const data = getDefaultData();
                        Object.keys(data).forEach((key) => {
                            this[key] = data[key];
                        });
                    })
                    .catch(() => {
                        showErrorMessage(
                            this.trans('pages.workpostcutting.errors.unknown')
                        );
                        this.saving = false;
                    });
            });
        },
        onPause() {
            this.pausingOrRestarting = true;
            this.finish(true)
                .then(() => {
                    showInfoMessage(
                        this.trans(
                            'pages.workpostcutting.messages.production_time_saved'
                        )
                    );
                })
                .finally(() => {
                    this.pausingOrRestarting = false;
                });
        },
        onErrorReporting() {
            this.onPause();
            this.$router.push('/productsaw/' + this.selectedProduct.productId);
        },
        startTimer(restart = false) {
            if (restart) {
                this.pausingOrRestarting = true;
            } else {
                this.loadingTimer = true;
            }

            productionTimesApi
                .fetchPleatingList(this.pleatinglist.id, this.workstation.id)
                .then((response) => {
                    const timerData = response.data.data;
                    if (timerData.missingMinutes.length > 0) {
                        const str = timerData.missingMinutes
                            .map((x) => '#' + x)
                            .join(', ');
                        const plural =
                            timerData.missingMinutes.length == 1 ? '' : 's';
                        showInfoMessage(
                            this.trans(
                                'pages.workpostcutting.messages.missing_target_times',
                                { plural: plural, types: str }
                            )
                        );
                    }
                    this.timer = createAggregatedTimer(timerData);
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.workpostcutting.errors.no_timer')
                    );
                })
                .finally(() => {
                    if (restart) {
                        this.pausingOrRestarting = false;
                    } else {
                        this.loadingTimer = false;
                    }
                });
        },
        scan() {
            this.$bus.$emit('qr_scanner.start', 'workpostcutting', {
                singleScan: true,
                defaultCamera: 'front',
                mocker: pleatingListMocker,
            });
        },
        onScan(value) {
            let decoded = null;
            try {
                decoded = JSON.parse(value);
            } catch (error) {}

            if (decoded && decoded.context === 'pleatinglist') {
                this.scanNr = decoded.pleatinglistId;
                this.scanNrBlur();
            } else {
                showErrorMessage(
                    this.trans('pages.workpostcutting.errors.invalid_qr'),
                    null,
                    1500
                );
            }
        },
        onFinishedCheckbox(cutline) {
            const isFinished = cutline.sawn == '1';

            sawlistApi
                .updateSawnForPleatingSawline(
                    parseInt(cutline.paintlistRecordSawlineId),
                    isFinished
                )
                .then(() => {
                    const allFinished = this.allCutlines.every(
                        (x) => x.sawn == '1'
                    );

                    const records = this.getRecords(
                        this.selectedProduct.productId
                    );
                    records.forEach((record) => {
                        const sawlistItem = (record.sawlist || []).find(
                            (x) => x.ID == cutline.ID
                        );

                        if (sawlistItem) {
                            sawlistItem.sawn = cutline.sawn;
                        }
                        record.sawlinesSawn = allFinished ? '1' : '0';
                    });
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.workpostcutting.errors.unknown')
                    );
                    cutline.sawn = isFinished ? '0' : '1';
                });
        },
        openProductRecords(product) {
            if (this.selectedProduct && this.selectedProduct.ID == product.ID) {
                this.allCutlines = null;
                this.selectedProduct = null;
            } else {
                this.selectedProduct = product;
                const productId = product.productId;
                const profiles = this.getRecords(parseInt(productId));
                this.loadCutlines(profiles);
            }
        },
        getRecords(id) {
            const records = [];
            if (this.pleatinglist && this.pleatinglist.profiles) {
                this.pleatinglist.profiles.forEach((record) => {
                    if (record.products) {
                        const any = record.products.some((x) => x == id);
                        if (any) {
                            records.push(record);
                        }
                    }
                });
            }
            return records;
        },
        getBollStyle(product) {
            const records = this.getRecords(product.productId);
            return {
                'background-color': records.every((x) => x.sawlinesSawn != '0')
                    ? 'green'
                    : 'red',
            };
        },
        getCreationFilter(product) {
            return product.datafields.find(
                (x) =>
                    (x.datafieldId = product.productType.pleatingCreationFilter)
            );
        },
        getSelectedCutlines() {
            if (!this.allCutlines) {
                return [];
            }
            return this.allCutlines.filter((x) => x.selected);
        },
        scanNrBlur() {
            this.allCutlines = null;
            this.pleatinglist = null;

            const id = this.scanNr;
            if (id == '') {
                return;
            }

            this.loadingCutlines = true;
            pleatingListApi
                .fetchDetails(parseInt('0' + id.replace(/\D/g, '')), true)
                .then((response) => {
                    const details = response.data.data;

                    details.products.forEach((product) => {
                        product.imgUrl =
                            Config.url +
                            '/product/customImage/?productId=' +
                            product.productNr +
                            '&random=' +
                            Math.random();
                    });

                    this.pleatinglist = details;

                    this.scanNr = details.nr;

                    this.startTimer();
                })
                .catch((error) => {
                    if (error.response.status == 404) {
                        showErrorMessage(
                            this.trans(
                                'pages.workpostcutting.errors.no_pleating_list'
                            )
                        );
                    } else {
                        showErrorMessage(
                            this.trans('pages.workpostcutting.errors.unknown')
                        );
                    }
                })
                .finally(() => {
                    this.loadingCutlines = false;
                    this.allCutlines = null;
                });
        },
        getMiter(sawline) {
            let miter = '';

            const leftEnd = parseInt(sawline.leftEnd);
            if (leftEnd == 0) {
                miter += '| ';
            } else if (leftEnd >= 1) {
                miter += '\\ ';
            }

            const rightEnd = parseInt(sawline.rightEnd);
            if (rightEnd == 0) {
                miter += '|';
            } else if (rightEnd >= 1) {
                miter += '/';
            }

            return miter;
        },
        loadCutlines(records) {
            this.loadingCutlines = true;
            this.allCutlines = [];
            const promises = records.map((record) => {
                return sawlistApi
                    .fetchSawlinesForPleatinglistrecord(record.ID)
                    .then((response) => {
                        response.data.data.sawlist.forEach((cutline) => {
                            cutline.selected = false;
                            this.allCutlines.push(cutline);
                        });
                    });
            });

            Promise.all(promises).then(() => {
                this.loadingCutlines = false;
            });
        },
        printOrExport(args) {
            if (KeyHelper.isPressed('Shift')) {
                const url = api.createGetUrl(
                    '/export/' + 'export' + args.partialEndpointName,
                    args.payload
                );
                window.open(url, '_blank');
            } else {
                this.printing[args.key] = true;
                axios
                    .post(
                        '/export/print' + args.partialEndpointName + '/',
                        args.payload
                    )
                    .then(() => {
                        showInfoMessage(
                            this.trans(
                                'pages.workpostcutting.messages.labels_printed'
                            )
                        );
                    })
                    .catch(() => {
                        showErrorMessage(
                            this.trans('pages.workpostcutting.errors.unknown')
                        );
                    })
                    .finally(() => {
                        this.printing[args.key] = false;
                    });
            }
        },
        printPRforLine() {
            const records = this.getSelectedCutlines();
            if (records.length == 0) {
                return showErrorMessage(
                    this.trans('pages.workpostcutting.errors.select_line')
                );
            }
            this.printOrExport({
                partialEndpointName: 'ProductionLabelsForPleatinglistRecords',
                key: 'linePR',
                payload: {
                    recordId: records[0].ID,
                    preferredPrinter: this.selectedPrinterPostParameter,
                    employeeId: parseInt(
                        this.$store.getters['currentUser/getUserId'] || -1
                    ),
                },
            });
        },
        printAllPR() {
            this.printOrExport({
                partialEndpointName: 'ProductionLabelsForPleatinglist',
                key: 'PR',
                payload: {
                    pleatingListId: this.pleatinglist.id,
                    preferredPrinter: this.selectedPrinterPostParameter,
                    employeeId: parseInt(
                        this.$store.getters['currentUser/getUserId'] || -1
                    ),
                },
            });
        },
    },
};
</script>
